/**
 * Operating Agreement Import Mapper — Deterministic Bridge
 *
 * Pure JavaScript module that converts operating-agreement-import/v1 JSON
 * into the flat variables dict that docxtpl renders into the OA templates.
 *
 * No AI. No API calls. No side effects.
 *
 * Mirrors the shape of briefImportMapper.js. Diverges on the canonical-model
 * step: OA has no fiduciary hierarchy, so the import goes directly to a flat
 * variables dict. Derived booleans (is_s_corp, has_ab_classes, etc.) are
 * precomputed here so the docxtpl templates can use plain {% if %} blocks
 * and the backend evaluate_condition() stays untouched.
 *
 * The package_document_mapping mirrors s3_assets/metadata.json so the mapper
 * and metadata are aligned without runtime cross-talk.
 */

const SUPPORTED_VERSIONS = ['1.0'];

const SUPPORTED_PACKAGES = [
  'Operating Agreement Package - Business',
  'Operating Agreement Package - FLP',
];

export const PACKAGE_DOCUMENT_MAPPING = {
  'Operating Agreement Package - Business': {
    'Operating Agreement': 'operating_agreements/oa-business.docx',
    'Operating Agreement Summary': 'operating_agreements/oa-business-summary.docx',
    'Schedule C': 'operating_agreements/oa-business-schedule-c.docx',
  },
  'Operating Agreement Package - FLP': {
    'Operating Agreement': 'operating_agreements/oa-flp.docx',
    'Operating Agreement Summary': 'operating_agreements/oa-flp-summary.docx',
    'Schedule C': 'operating_agreements/oa-flp-schedule-c.docx',
  },
};

// ---------------------------------------------------------------------------
// 1. Schema Validation
// ---------------------------------------------------------------------------

export function validateImportSchema(json) {
  const errors = [];

  if (!json || typeof json !== 'object') {
    return { valid: false, errors: ['Input is not a valid JSON object.'] };
  }
  if (json.schema_name !== 'operating-agreement-import') {
    errors.push(`Invalid schema_name: expected "operating-agreement-import", got "${json.schema_name}".`);
  }
  if (!SUPPORTED_VERSIONS.includes(json.schema_version)) {
    errors.push(`Unsupported schema_version "${json.schema_version}". Supported: ${SUPPORTED_VERSIONS.join(', ')}.`);
  }
  if (!('package_candidate' in json)) {
    errors.push('Missing required field: package_candidate (may be null).');
  }
  if (!json.company || typeof json.company !== 'object' || !json.company.name) {
    errors.push('Missing required field: company.name.');
  }
  if (!json.tax || typeof json.tax !== 'object' || !json.tax.classification) {
    errors.push('Missing required field: tax.classification.');
  }
  if (!Array.isArray(json.members) || json.members.length === 0) {
    errors.push('Missing required field: members (must be a non-empty array).');
  }

  return { valid: errors.length === 0, errors };
}

// ---------------------------------------------------------------------------
// 2. Package Resolution
// ---------------------------------------------------------------------------

export function resolvePackage(importData) {
  const errors = [];
  const warnings = [];
  const candidate = importData.package_candidate;

  if (!candidate) {
    errors.push('Package type required. Select "Operating Agreement Package - Business" or "Operating Agreement Package - FLP".');
    return { package: null, errors, warnings };
  }
  if (!SUPPORTED_PACKAGES.includes(candidate)) {
    errors.push(`Unknown or unsupported package: "${candidate}".`);
    return { package: null, errors, warnings };
  }
  return { package: candidate, errors, warnings };
}

// ---------------------------------------------------------------------------
// 3. Variable Builder
// ---------------------------------------------------------------------------

const NUM_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];

function toNumberWord(n) {
  if (typeof n !== 'number' || n < 0 || n > 10) return String(n);
  return NUM_WORDS[n];
}

function formatPercent(p) {
  if (p === null || p === undefined) return '';
  return Number.isInteger(p) ? `${p}%` : `${p}%`;
}

function formatMoney(amount) {
  if (amount === null || amount === undefined) return '';
  return '$' + amount.toLocaleString('en-US');
}

function taxClassificationPhrase(classification) {
  switch (classification) {
    case 'partnership': return 'a partnership';
    case 's_corp': return 'an S corporation';
    case 'c_corp': return 'a C corporation';
    case 'disregarded': return 'a disregarded entity';
    default: return classification;
  }
}

function joinNames(names) {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

export function buildVariables(importData) {
  const v = {};

  // Engagement
  const eng = importData.engagement || {};
  v.matter_id = eng.matter_id || '';
  v.engagement_date = eng.engagement_date || '';
  v.drafting_attorney = eng.drafting_attorney || '';
  v.paralegal_name = eng.paralegal_name || '';

  // Company
  const company = importData.company || {};
  v.company_name = company.name || '';
  v.company_name_upper = (company.name || '').toUpperCase();
  v.company_name_no_comma = (company.name || '').replace(/,/g, '');
  v.state_of_formation = company.state_of_formation || '';
  v.state_of_formation_lower = (company.state_of_formation || '').toLowerCase();
  v.principal_office = company.principal_office || '';
  v.principal_office_short = company.principal_office_short || company.principal_office || '';
  v.purpose = company.purpose || '';
  v.business_descriptor = company.business_descriptor || company.purpose || '';
  v.effective_date = company.effective_date || '';
  v.fiscal_year_end = company.fiscal_year_end || '';

  // Tax — both raw and phrase forms; mapper precomputes booleans
  const tax = importData.tax || {};
  v.tax_classification = tax.classification || '';
  v.tax_classification_phrase = taxClassificationPhrase(tax.classification);
  v.is_disregarded = tax.classification === 'disregarded';
  v.is_partnership = tax.classification === 'partnership';
  v.is_s_corp = tax.classification === 's_corp';
  v.is_c_corp = tax.classification === 'c_corp';

  // Class structure
  const cls = importData.class_structure || {};
  v.has_ab_classes = !!cls.has_ab_classes;
  v.class_a_definition = cls.class_a_definition || '';
  v.class_b_definition = cls.class_b_definition || '';

  // Members
  const members = importData.members || [];
  v.member_count = members.length;
  v.member_count_word = toNumberWord(members.length);
  v.member_count_label = members.length === 1 ? 'sole Member' : 'initial Members';
  v.members = members.map(m => ({
    full_name: m.full_name || '',
    address: m.address || '',
    percentage_interest: m.percentage_interest,
    percentage_phrase: formatPercent(m.percentage_interest),
    capital_contribution: m.capital_contribution || '',
    is_manager: !!m.is_manager,
  }));

  // Flat per-member tokens for the common 2-member case so simple substitution
  // works in templates. For N>2, use the {{members}} list with a Jinja for loop.
  for (let i = 0; i < members.length; i++) {
    const m = members[i];
    v[`member${i + 1}_name`] = m.full_name || '';
    v[`member${i + 1}_percent`] = formatPercent(m.percentage_interest);
    v[`member${i + 1}_address`] = m.address || '';
    v[`member${i + 1}_capital_contribution`] = m.capital_contribution || '';
  }

  // Co-managers (members where is_manager === true)
  const managers = members.filter(m => m.is_manager);
  v.manager_count = managers.length;
  v.manager_names_joined = joinNames(managers.map(m => m.full_name));
  for (let i = 0; i < managers.length; i++) {
    v[`co_manager${i + 1}_name`] = managers[i].full_name || '';
  }

  // Management
  const mgmt = importData.management || {};
  v.management_mode = mgmt.mode || 'manager_managed';
  v.is_manager_managed = (mgmt.mode || 'manager_managed') === 'manager_managed';
  v.veto_threshold_pct = mgmt.veto_threshold_pct ?? 50;
  v.veto_supermajority = (mgmt.veto_threshold_pct ?? 50) > 50;
  v.asset_sale_threshold_amount = mgmt.asset_sale_threshold_amount ?? 100000;
  v.asset_sale_threshold_phrase = formatMoney(mgmt.asset_sale_threshold_amount ?? 100000);
  v.majority_can_remove_co_manager = mgmt.majority_can_remove_co_manager ?? true;

  // Buy-sell
  const bs = importData.buy_sell || {};
  v.death_buyout_mode = bs.death_buyout_mode || 'mandatory_cross_purchase';
  v.death_buyout_mandatory = (bs.death_buyout_mode || 'mandatory_cross_purchase') !== 'optional';
  v.disability_days = bs.disability_days ?? 180;
  v.disability_reduction_pct = bs.disability_reduction_pct ?? 50;
  v.disability_reduction_phrase = `${bs.disability_reduction_pct ?? 50}%`;
  v.departure_voluntary_optional = (bs.departure_voluntary_mode || 'optional') === 'optional';
  v.departure_involuntary_mandatory = (bs.departure_involuntary_mode || 'mandatory') === 'mandatory';
  v.involuntary_reduction_pct = bs.involuntary_reduction_pct ?? 50;
  v.insurance_funding = bs.insurance_funding || 'recommend';
  v.insurance_mandate = (bs.insurance_funding || 'recommend') === 'mandate';
  v.insurance_recommend = (bs.insurance_funding || 'recommend') === 'recommend';

  // Pricing
  const pr = importData.pricing || {};
  v.covered_period_default_months = pr.covered_period_default_months ?? 12;
  v.covered_period_max_months = pr.covered_period_max_months ?? 24;

  // Payment
  const pay = importData.payment || {};
  v.note_term_months = pay.note_term_months ?? 60;
  v.interest_rate_expression = pay.interest_rate_expression || 'prime rate plus 1.0%';
  v.interest_rate_short_expression = pay.interest_rate_short_expression || pay.interest_rate_expression || 'prime rate plus 1.0%';
  v.rofr_acceptance_days = pay.rofr_acceptance_days ?? 30;
  v.rofr_sale_window_days = pay.rofr_sale_window_days ?? 90;

  // Transferees
  const tr = importData.transferees || {};
  v.transferees_scope = tr.scope || 'rlt_only';
  v.transferees_rlt_only = (tr.scope || 'rlt_only') === 'rlt_only';
  v.transferees_custom_definition = tr.custom_definition || '';

  // Distributions
  const dist = importData.distributions || {};
  v.tax_distribution_mandatory = dist.tax_distribution_mandatory ?? true;
  v.capital_calls_mode = dist.capital_calls_mode || 'permitted';
  v.capital_calls_permitted = (dist.capital_calls_mode || 'permitted') === 'permitted';
  v.capital_calls_mandatory = (dist.capital_calls_mode || 'permitted') === 'mandatory';
  v.capital_calls_prohibited = (dist.capital_calls_mode || 'permitted') === 'prohibited';

  // Disputes
  const dsp = importData.disputes || {};
  v.dispute_forum = dsp.forum || 'mediation_first_then_arb';
  v.governing_law_state = dsp.governing_law_state || company.state_of_formation || '';
  v.arbitration_provider = dsp.arbitration_provider || '';
  v.venue_county = dsp.venue_county || '';
  v.dispute_negotiation_days = dsp.negotiation_days ?? 15;

  // Firm (used by summary memo and §15.11)
  const firm = importData.firm || {};
  v.firm_name = firm.name || '';

  // Representation
  const rep = importData.representation || {};
  v.represented_member_name = rep.represented_member_name || '';
  v.represents_entity = !!rep.represents_entity;
  v.conflict_waiver_attached = !!rep.conflict_waiver_attached;
  // Other-members phrase: joined list of members not represented by the firm,
  // used by §15.11 and the summary memo's representation disclosure.
  const otherMembers = members.filter(m => m.full_name !== rep.represented_member_name).map(m => m.full_name).filter(Boolean);
  v.other_member_names_joined = joinNames(otherMembers);

  // Deliverables
  const dlv = importData.deliverables || {};
  v.include_summary_memo = dlv.include_summary_memo ?? true;
  v.include_schedule_c = dlv.include_schedule_c ?? true;

  return v;
}

// ---------------------------------------------------------------------------
// 4. Public Entry Point
// ---------------------------------------------------------------------------

export function mapOperatingAgreementImport(json) {
  const schemaCheck = validateImportSchema(json);
  if (!schemaCheck.valid) {
    return { errors: schemaCheck.errors, warnings: [], package: null, documentPaths: {}, variables: {} };
  }
  const pkgResolution = resolvePackage(json);
  if (pkgResolution.errors.length > 0) {
    return { errors: pkgResolution.errors, warnings: pkgResolution.warnings, package: null, documentPaths: {}, variables: {} };
  }
  const variables = buildVariables(json);
  const documentPaths = { ...PACKAGE_DOCUMENT_MAPPING[pkgResolution.package] };
  if (!variables.include_summary_memo) delete documentPaths['Operating Agreement Summary'];
  if (!variables.include_schedule_c) delete documentPaths['Schedule C'];
  return {
    errors: [],
    warnings: pkgResolution.warnings,
    package: pkgResolution.package,
    documentPaths,
    variables,
  };
}
